import React, { useRef } from "react";
import { motion } from "framer-motion";
import { Link } from "react-router-dom";

import {
  FaMapMarkerAlt,
  FaPaperPlane,
  FaGlobeAmericas,
  FaChevronRight,
  FaCheckCircle,
  FaStar,
  FaChevronDown,
  FaTimesCircle,
  FaBus,
  FaHotel,
  FaUtensils,
  FaShieldAlt,
  FaMountain,
  FaRoad,
} from "react-icons/fa";

import "./ArunachalPradesh.css";

const ArunachalPradesh = () => {

  const itineraryRef = useRef(null);
  const highlightsRef = useRef(null);
  const inclusionsRef = useRef(null);
  const aboutRef = useRef(null);

  const scrollToSection = (ref) => {
    window.scrollTo({
      top: ref.current.offsetTop - 100,
      behavior: "smooth",
    });
  };

  const fadeInUp = {
    hidden: { opacity: 0, y: 40 },
    visible: {
      opacity: 1,
      y: 0,
      transition: {
        duration: 0.7,
        ease: "easeOut",
      },
    },
  };

  const itineraryData = [
    {
      day: 1,
      title: "Arrival in Guwahati | Welcome to Assam",
      points: [
        "Arrival at Guwahati Airport or Railway Station.",
        "Transfer to your hotel and complete check-in.",
        "Optional visit to Kamakhya Temple depending on arrival time.",
        "Relax and enjoy the local atmosphere of Guwahati.",
        "Overnight stay in Guwahati.",
      ],
      icon: <FaRoad />,
    },

    {
      day: 2,
      title: "Guwahati to Bomdila | Entering Arunachal Pradesh",
      points: [
        "After breakfast begin your journey towards Bomdila.",
        "Drive through scenic foothills, rivers, and forests.",
        "Complete Arunachal Pradesh permit formalities en route.",
        "Visit the peaceful Bomdila Monastery.",
        "Enjoy beautiful mountain views from Bomdila.",
        "Overnight stay in Bomdila.",
      ],
      icon: <FaMountain />,
    },

    {
      day: 3,
      title: "Bomdila to Dirang | Valley Exploration",
      points: [
        "After breakfast proceed towards Dirang.",
        "Visit the historic Dirang Dzong.",
        "Explore natural Hot Water Springs.",
        "Discover the scenic Sangti Valley and Monpa villages.",
        "Enjoy peaceful valley landscapes and mountain views.",
        "Overnight stay in Dirang.",
      ],
      icon: <FaMountain />,
    },

    {
      day: 4,
      title: "Dirang to Tawang via Sela Pass",
      points: [
        "Depart early morning for Tawang.",
        "Cross the breathtaking Sela Pass at 13,700 ft.",
        "Visit the serene Sela Lake near the pass.",
        "Stop at Jaswant Garh War Memorial.",
        "Enjoy scenic Himalayan landscapes throughout the drive.",
        "Arrive in Tawang and check in to your hotel.",
      ],
      icon: <FaRoad />,
    },

    {
      day: 5,
      title: "Tawang Local Sightseeing",
      points: [
        "Visit the famous Tawang Monastery.",
        "Explore the Tawang War Memorial.",
        "Visit Urgeling Monastery, birthplace of the 6th Dalai Lama.",
        "Explore the Tawang Craft Centre and local handicrafts.",
        "Enjoy leisure time at local cafés and markets.",
        "Overnight stay in Tawang.",
      ],
      icon: <FaCheckCircle />,
    },

    {
      day: 6,
      title: "Bum La Pass & Madhuri Lake Excursion",
      points: [
        "Proceed for a full-day high-altitude excursion.",
        "Visit the historic Bum La Pass near the Indo-China border.",
        "Explore the beautiful Madhuri Lake (Sangetsar Lake).",
        "Enjoy views of snow-covered mountains and scenic lakes.",
        "Visit additional viewpoints and high-altitude lakes.",
        "Return to Tawang by evening.",
      ],
      icon: <FaMountain />,
    },

    {
      day: 7,
      title: "Tawang to Dirang | Scenic Return Journey",
      points: [
        "After breakfast begin your return drive towards Dirang.",
        "Cross the scenic Sela Pass once again.",
        "Stop for photography and scenic viewpoints en route.",
        "Enjoy the beautiful Himalayan landscapes.",
        "Arrive in Dirang and relax.",
        "Overnight stay in Dirang.",
      ],
      icon: <FaRoad />,
    },

    {
      day: 8,
      title: "Dirang to Guwahati | Departure",
      points: [
        "After breakfast depart towards Guwahati.",
        "Drive through scenic Himalayan foothills and Assam plains.",
        "Transfer to Airport or Railway Station on arrival.",
        "Tour concludes with unforgettable Arunachal memories.",
      ],
      icon: <FaCheckCircle />,
    },
  ];

  return (
    <div className="trip-container">

      {/* HEADER */}
      <motion.header
        initial="hidden"
        whileInView="visible"
        viewport={{ once: false }}
        variants={fadeInUp}
        className="trip-header"
      >

        <div className="header-left">

          <h1 className="trip-title">
            Classic Arunachal Pradesh – Tawang Circuit
          </h1>

          <p className="trip-duration">
            7 Nights / 8 Days
          </p>

        </div>

        <div className="header-right">

          <div className="info-row">

            <FaMapMarkerAlt className="header-icon" />

            <div className="info-text">
              <span>Destinations:</span>

              <p>
                Guwahati • Bomdila • Dirang • Tawang • Sela Pass
              </p>
            </div>

          </div>

          <div className="info-row">

            <FaPaperPlane className="header-icon" />

            <div className="info-text">
              <span>Start & End:</span>
              <p>Guwahati</p>
            </div>

          </div>

          <div className="info-row type-tag">

            <FaGlobeAmericas className="header-icon" />

            <p>
              Mountains • Monasteries • Nature • Culture
            </p>

          </div>

        </div>

      </motion.header>

      {/* IMAGE GRID */}
      <motion.div
        initial="hidden"
        whileInView="visible"
        viewport={{ once: false }}
        variants={fadeInUp}
        className="image-grid-container"
      >

        <div className="grid-item large-vertical">
          <img
            src="https://images.pexels.com/photos/17574949/pexels-photo-17574949.jpeg"
            alt="Tawang"
          />
        </div>

        <div className="grid-item middle-box">
          <img
            src="https://images.pexels.com/photos/2815093/pexels-photo-2815093.jpeg"
            alt="Sela Pass"
          />
        </div>

        <div className="right-stack">

          <div className="grid-item small-h">
            <img
              src="https://images.pexels.com/photos/35392521/pexels-photo-35392521.jpeg"
              alt="Mountain Lake"
            />
          </div>

          <div className="grid-item small-h">
            <img
              src="https://images.unsplash.com/photo-1441974231531-c6227db76b6e?q=80&w=1200&auto=format&fit=crop"
              alt="Valley"
            />
          </div>

        </div>

      </motion.div>

      {/* STICKY NAV */}
      <nav className="sticky-nav">

        <button
          className="tab-link"
          onClick={() => scrollToSection(itineraryRef)}
        >
          Detailed Itinerary
        </button>

        <button
          className="tab-link"
          onClick={() => scrollToSection(highlightsRef)}
        >
          Highlights
        </button>

        <button
          className="tab-link"
          onClick={() => scrollToSection(inclusionsRef)}
        >
          Policy & Inclusions
        </button>

        <button
          className="tab-link"
          onClick={() => scrollToSection(aboutRef)}
        >
          About Trip
        </button>

      </nav>

      <div className="content-section-wrapper">

        {/* ITINERARY */}
        <section ref={itineraryRef} className="scroll-section">

          <h2 className="section-main-title">
            Tour Itinerary
          </h2>

          <div className="days-list">

            {itineraryData.map((item) => (
              <motion.div
                key={item.day}
                initial="hidden"
                whileInView="visible"
                viewport={{ once: false, amount: 0.2 }}
                variants={fadeInUp}
                className="day-block"
              >

                <div className="day-header">

                  <span className="day-badge">
                    Day {item.day}
                  </span>

                  <h3>{item.title}</h3>

                </div>

                <div className="day-body">

                  {item.points.map((p, i) => (
                    <div key={i} className="point-item">

                      <FaChevronRight className="bullet-icon" />

                      <p>{p}</p>

                    </div>
                  ))}

                </div>

              </motion.div>
            ))}

          </div>

        </section>

        {/* HIGHLIGHTS */}
        <motion.section
          ref={highlightsRef}
          initial="hidden"
          whileInView="visible"
          viewport={{ once: false }}
          variants={fadeInUp}
          className="scroll-section"
        >

          <h2 className="section-main-title">
            Trip Highlights
          </h2>

          <div className="highlights-grid">

            <div className="h-item">
              <FaStar className="star-icon" />
              <p>
                Visit the world-famous Tawang Monastery
              </p>
            </div>

            <div className="h-item">
              <FaStar className="star-icon" />
              <p>
                Cross the breathtaking Sela Pass at 13,700 ft
              </p>
            </div>

            <div className="h-item">
              <FaStar className="star-icon" />
              <p>
                Explore Madhuri Lake and scenic high-altitude lakes
              </p>
            </div>

            <div className="h-item">
              <FaStar className="star-icon" />
              <p>
                Experience traditional Monpa culture in Dirang
              </p>
            </div>

            <div className="h-item">
              <FaStar className="star-icon" />
              <p>
                Visit Jaswant Garh and Tawang War Memorial
              </p>
            </div>

            <div className="h-item">
              <FaStar className="star-icon" />
              <p>
                Enjoy scenic Himalayan drives and alpine forests
              </p>
            </div>

          </div>

        </motion.section>

        {/* INCLUSIONS */}
        <motion.section
          ref={inclusionsRef}
          initial="hidden"
          whileInView="visible"
          viewport={{ once: false }}
          variants={fadeInUp}
          className="scroll-section"
        >

          <div className="policy-box inclusion-box">

            <h4 className="p-title green">

              <FaCheckCircle />
              What's Included

            </h4>

            <ul className="p-list">

              <li>
                <FaHotel />
                Accommodation for 7 nights
              </li>

              <li>
                <FaBus />
                Transportation and sightseeing from Guwahati to Guwahati
              </li>

              <li>
                <FaCheckCircle />
                Inner Line Permit (ILP) for Indian nationals
              </li>

              <li>
                <FaCheckCircle />
                Sightseeing as mentioned in itinerary
              </li>

              <li>
                <FaShieldAlt />
                Driver allowance, fuel, tolls, and parking
              </li>

              <li>
                <FaShieldAlt />
                Tour coordination and on-trip assistance
              </li>

            </ul>

          </div>

          <div className="policy-box exclusion-box">

            <h4 className="p-title red">

              <FaTimesCircle />
              What's Excluded

            </h4>

            <ul className="p-list">

              <li>
                <FaTimesCircle />
                Airfare or train tickets
              </li>

              <li>
                <FaUtensils />
                Lunch and meals not mentioned
              </li>

              <li>
                <FaTimesCircle />
                Entry fees at sightseeing locations
              </li>

              <li>
                <FaTimesCircle />
                Bum La special permits if applicable
              </li>

              <li>
                <FaTimesCircle />
                Personal expenses and shopping
              </li>

              <li>
                <FaShieldAlt />
                Travel insurance or emergency evacuation
              </li>

              <li>
                <FaTimesCircle />
                Expenses due to weather or roadblocks
              </li>

              <li>
                <FaTimesCircle />
                Services not mentioned in inclusions
              </li>

            </ul>

          </div>

        </motion.section>

        {/* ABOUT */}
        <motion.section
          ref={aboutRef}
          initial="hidden"
          whileInView="visible"
          viewport={{ once: false }}
          variants={fadeInUp}
          className="scroll-section"
        >

          <h2 className="section-main-title">
            About This Journey
          </h2>

          <p className="about-text">

            The Classic Arunachal Pradesh – Tawang Circuit is one of the most
            popular and well-balanced journeys through the eastern Himalayas.
            This itinerary covers iconic destinations of Arunachal Pradesh,
            including serene monasteries, dramatic mountain passes,
            high-altitude lakes, and culturally rich valleys.
            <br /><br />
            Carefully paced for proper acclimatization and comfortable travel,
            this tour is ideal for families, senior travelers, and first-time
            visitors to the Northeast. From the spiritual calm of Tawang
            Monastery to the breathtaking landscapes of Sela Pass and Bum La,
            the journey offers a perfect blend of nature, culture, and history.

          </p>

        </motion.section>

        {/* TERMS */}
        <div className="accordion-item">

          <Link
            to="/terms-conditions"
            className="accordion-header"
            style={{
              textDecoration: "none",
              color: "inherit",
            }}
          >

            <span>Terms & Conditions</span>

            <FaChevronDown
              className="arrow-icon"
              style={{ transform: "rotate(-90deg)" }}
            />

          </Link>

        </div>

        {/* PRIVACY */}
        <div className="accordion-item">

          <Link
            to="/privacy-policy"
            className="accordion-header"
            style={{
              textDecoration: "none",
              color: "inherit",
            }}
          >

            <span>Privacy Policy</span>

            <FaChevronDown
              className="arrow-icon"
              style={{ transform: "rotate(-90deg)" }}
            />

          </Link>

        </div>

        {/* IMPORTANT NOTES */}
        <motion.section
          initial="hidden"
          whileInView="visible"
          viewport={{ once: false }}
          variants={fadeInUp}
          className="scroll-section travel-info-grid"
        >

          <div
            className="info-card note-card"
            style={{ width: "100%" }}
          >

            <h4 className="info-card-title">

              <FaPaperPlane />
              Important Notes

            </h4>

            <p>
              • Bum La Pass visit depends on permits and weather conditions.
              <br /><br />
              • High-altitude travel may require proper acclimatization.
              <br /><br />
              • Carry warm clothing and essential medicines during the tour.
              <br /><br />
              • Road conditions in mountain regions may vary depending on weather.
            </p>

          </div>

        </motion.section>

      </div>

    </div>
  );
};

export default ArunachalPradesh;